import { Button } from '@factorialco/deprecated-design-system'
import { DeleteSerialSequenceDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SerialSequencesPreviewBox } from 'modules/finance/components/SerialSequences/PreviewBox'

import { SerialSequenceLoadingModal } from '../SerialSequenceLoadingModal'
import { SerialSequenceNotFoundModal } from '../SerialSequenceNotFoundModal'
import { useSerialSequence } from '../useSerialSequence'

export const SerialSequenceDeleteModal = ({
  isOpen,
  onClose,
  refetchQuery,
  serialSequenceId,
}: {
  isOpen: boolean
  onClose: () => void
  refetchQuery?: string
  serialSequenceId: number
}) => {
  const { serialSequence, loading, notFound } = useSerialSequence(serialSequenceId, isOpen)

  const [deleteSerialSequence] = useMutation(DeleteSerialSequenceDocument, {
    extractorFn: (data) => data.financeMutations.deleteFinanceSerialSequenceSalesInvoiceSequence,
    refetchQueries: refetchQuery ? [refetchQuery] : undefined,
  })

  const onSubmit = async () => {
    const result = await deleteSerialSequence({
      variables: {
        id: serialSequenceId,
      },
    })

    const handled = handleResult(result, (parsedErrors) => {
      app.setFlash({
        type: 'wrong',
        message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.serial_sequences.modal.delete_success'),
        time: 2000,
      })
      onClose()
    }
  }

  if (!isOpen) return null

  if (loading) {
    return (
      <SerialSequenceLoadingModal
        title={i18n.t('finance.serial_sequences.modal.delete_sequence_title')}
        onClose={onClose}
      />
    )
  }

  if (notFound) {
    return (
      <SerialSequenceNotFoundModal
        title={i18n.t('finance.serial_sequences.modal.delete_sequence_title')}
        serialSequenceId={serialSequenceId}
        onClose={onClose}
      />
    )
  }

  if (!serialSequence) {
    return null
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('finance.serial_sequences.modal.delete_sequence_title')}
      description={i18n.t('finance.serial_sequences.modal.delete_sequence_description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit>{i18n.t('common.confirm')}</Button>
        </>
      }
    >
      <SerialSequencesPreviewBox
        name={serialSequence.name}
        preview={serialSequence.humanReadablePreview}
      />
    </Modal>
  )
}
